import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {
	ProtectedRoute,
	AppNavigationProvider,
} from '@practium/features/navigation';
import { ThemeProvider, ThemePreferenceProvider } from '@practium/features/theme';
import { AuthProvider, useAuth } from '@practium/features/auth';
import { ForBindingProvider, useForBinding, OwnerOnlyGuard } from '@practium/features/forBinding';
import { ToastProvider, Unauthorized, LoadingState } from '@practium/ui';
import { FloraAIPage, FloraSupportWidget } from '@practium/features/flora-ai';
import { UserRole, UserHierarchyLevel, createGetAppUrl, normalizeHashRedirect } from '@practium/shared';
import { AdminRedirectGuard } from './guards/AdminRedirectGuard';
import { SubscriptionGate } from './guards/SubscriptionGate';
import { PendingPaymentGuard } from './guards/PendingPaymentGuard';
import { DynamicLayout } from './features/layout/DynamicLayout';
import { DashboardPage } from './features/dashboard/pages/DashboardPage';
import { HomePageClient } from './features/home/pages/HomePageClient';
import { APP_ROUTES } from './config/app-routes.config';
import { LoginPageClient } from './features/auth/pages/LoginPageClient';
import { RegisterPageClient } from './features/auth/pages/RegisterPageClient';
import { ForgotPasswordPageClient } from './features/auth/pages/ForgotPasswordPageClient';
import { ResetPasswordPageClient } from './features/auth/pages/ResetPasswordPageClient';
import { PaymentSuccessPageClient } from './features/checkout/pages/PaymentSuccessPageClient';
import { PaymentFailedPageClient } from './features/checkout/pages/PaymentFailedPageClient';
import { CheckoutPlanPageClient } from './features/checkout/pages/CheckoutPlanPageClient';
import { CheckoutPaymentPageClient } from './features/checkout/pages/CheckoutPaymentPageClient';
import { usePersistPlanFromQuery } from './features/checkout/hooks/usePersistPlanFromQuery';
import { ProfilePageClient } from './features/profile/pages/ProfilePageClient';
import { ProfileEditPageClient } from './features/profile/pages/ProfileEditPageClient';
import { NotificationProvider } from '@practium/features/profile';
import { usePermissions } from '@practium/features/permissions';
import { ParcellingPageClient } from './features/parcelling/pages/ParcellingPageClient';
import { ProductsPageClient } from './features/products/pages/ProductsPageClient';
import { AddProductPageClient } from './features/products/pages/AddProductPageClient';
import { ViewProductPageClient } from './features/products/pages/ViewProductPageClient';
import { PropertiesPageClient } from './features/properties/pages/PropertiesPageClient';
import { AddPropertyPageClient } from './features/properties/pages/AddPropertyPageClient';
import { ViewPropertyPageClient } from './features/properties/pages/ViewPropertyPageClient';
import { SectorFloraSummaryPageClient } from './features/properties/pages/SectorFloraSummaryPageClient';
import { SupportPageClient } from './features/support/pages/SupportPageClient';
import { ProductRequestsPageClient } from './features/product-requests/pages/ProductRequestsPageClient';
import { RecommendationsPageClient } from './features/recommendations/pages/RecommendationsPageClient';
import { AddFoliarAdvicePageClient } from './features/recommendations/pages/AddFoliarAdvicePageClient';
import { ViewFoliarAdvicePageClient } from './features/recommendations/pages/ViewFoliarAdvicePageClient';
import { AddMaintenanceAdvicePageClient } from './features/recommendations/pages/AddMaintenanceAdvicePageClient';
import { ViewMaintenanceAdvicePageClient } from './features/recommendations/pages/ViewMaintenanceAdvicePageClient';
import { EditMaintenanceAdvicePageClient } from './features/recommendations/pages/EditMaintenanceAdvicePageClient';
import { AddCorrectionAdvicePageClient } from './features/recommendations/pages/AddCorrectionAdvicePageClient';
import { ViewCorrectionAdvicePageClient } from './features/recommendations/pages/ViewCorrectionAdvicePageClient';
import { EditCorrectionAdvicePageClient } from './features/recommendations/pages/EditCorrectionAdvicePageClient';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UsersPageClient } from './features/users/pages/UsersPageClient';
import { AddUserPageClient } from './features/users/pages/AddUserPageClient';
import { ViewUserPageClient } from './features/users/pages/ViewUserPageClient';
import { FieldDiaryPageClient } from './features/field-diary/FieldDiaryPageClient';
import { AnalysesPageClient } from './features/analyses/pages/AnalysesPageClient';
import { AddAnalysisPageClient } from './features/analyses/pages/AddAnalysisPageClient';
import { ViewAnalysisPageClient } from './features/analyses/pages/ViewAnalysisPageClient';
import { MyPlanPage } from '@practium/features/my-plan';
import { OcrQueueProvider } from '@practium/features/analyses';
import { OcrQueueWidget } from './features/analyses/components/OcrQueueWidget/OcrQueueWidget';

normalizeHashRedirect('/recuperar-senha');

function AppRoutes() {
	usePersistPlanFromQuery();
	const { user } = useAuth();
	const { pathname } = useLocation();
	const { isForBinding, forBindingUser, forBindingUserLevel } = useForBinding();
	const { abilities, routePermissions, isLoading: permissionsLoading } = usePermissions({
		enabled: !!user,
	});

	const effectiveLevel = isForBinding
		? (forBindingUserLevel ?? UserHierarchyLevel.Farmer)
		: (user?.hierarchyLevel ?? UserHierarchyLevel.Farmer);

	const effectiveRole = isForBinding ? forBindingUser?.role : user?.role;
	const guard = (can: boolean, element: React.ReactElement) => {
		if (permissionsLoading) return <LoadingState fullScreen />;
		return can ? element : <Navigate to="/" replace />;
	};

	const canAccessProps = abilities.can('read', isForBinding ? 'farmers-props' : 'props');
	const canAccessRecommendations = abilities.can(
		'read',
		isForBinding ? 'farmers-recommendations' : 'recommendations'
	);
	const canAccessSamples = abilities.can('read', isForBinding ? 'farmers-samples' : 'samples');

	const canAccessUsers = isForBinding
		? forBindingUserLevel === UserHierarchyLevel.Consultant && abilities.can('read', 'farmers')
		: routePermissions.canAccessUsers;

	return (
		<>
			<Routes>
				<Route path="/login" element={<LoginPageClient />} />
				<Route path="/register" element={<RegisterPageClient />} />
				<Route path="/forgot-password" element={<ForgotPasswordPageClient />} />
				<Route path="/recuperar-senha" element={<ResetPasswordPageClient />} />
				<Route path="/billing/success" element={<PaymentSuccessPageClient />} />
				<Route path="/billing/error" element={<PaymentFailedPageClient />} />
				<Route path="/unauthorized" element={<Unauthorized />} />

				<Route
					element={
						<ProtectedRoute
							allowedRoles={[UserRole.Farmer, UserRole.Consultant, UserRole.Company]}
							allowAdmin={false}
							allowPendingSubscription={true}
						>
							<AdminRedirectGuard>
								<PendingPaymentGuard>
									<Outlet />
								</PendingPaymentGuard>
							</AdminRedirectGuard>
						</ProtectedRoute>
					}
				>
					<Route path="/checkout/plano" element={<CheckoutPlanPageClient />} />
					<Route path="/checkout/pagamento" element={<CheckoutPaymentPageClient />} />
					<Route path="/plan/upgrade" element={<Navigate to="/checkout/plano?upgrade=1" replace />} />
				</Route>

				<Route
					element={
						<ProtectedRoute
							allowedRoles={[UserRole.Farmer, UserRole.Consultant, UserRole.Company]}
							allowAdmin={true}
							allowPendingSubscription={true}
						>
							<AdminRedirectGuard>
								<SubscriptionGate>
									<DynamicLayout userLevel={effectiveLevel} />
								</SubscriptionGate>
							</AdminRedirectGuard>
						</ProtectedRoute>
					}
				>
					<Route path="/" element={<HomePageClient />} />
					<Route
						path="/dashboard"
						element={guard(routePermissions.canAccessDashboard, <DashboardPage />)}
					/>
					<Route path="/flora-ai" element={<FloraAIPage />} />
					<Route path="/propriedades" element={guard(canAccessProps, <PropertiesPageClient />)} />
					<Route
						path="/propriedades/novo"
						element={guard(canAccessProps, <AddPropertyPageClient />)}
					/>
					<Route
						path="/propriedades/ver"
						element={guard(canAccessProps, <ViewPropertyPageClient />)}
					/>
					<Route
						path="/propriedades/setores/:id/flora"
						element={guard(canAccessProps, <SectorFloraSummaryPageClient />)}
					/>
					<Route
						path="/recomendacoes"
						element={guard(canAccessRecommendations, <RecommendationsPageClient />)}
					/>
					<Route
						path="/recomendacoes/foliares/novo"
						element={guard(canAccessRecommendations, <AddFoliarAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/foliares/ver"
						element={guard(canAccessRecommendations, <ViewFoliarAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/manutencao/novo"
						element={guard(canAccessRecommendations, <AddMaintenanceAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/manutencao/workspace"
						element={guard(canAccessRecommendations, <ViewMaintenanceAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/manutencao/editar"
						element={guard(canAccessRecommendations, <EditMaintenanceAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/correcao/novo"
						element={guard(canAccessRecommendations, <AddCorrectionAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/correcao/workspace"
						element={guard(canAccessRecommendations, <ViewCorrectionAdvicePageClient />)}
					/>
					<Route
						path="/recomendacoes/correcao/editar"
						element={guard(canAccessRecommendations, <EditCorrectionAdvicePageClient />)}
					/>
					<Route path="/usuarios" element={guard(canAccessUsers, <UsersPageClient />)} />
					<Route path="/usuarios/novo" element={guard(canAccessUsers, <AddUserPageClient />)} />
					<Route path="/usuarios/ver" element={guard(canAccessUsers, <ViewUserPageClient />)} />
					<Route
						path="/produtos"
						element={
							<OwnerOnlyGuard>
								{guard(routePermissions.canAccessProducts, <ProductsPageClient />)}
							</OwnerOnlyGuard>
						}
					/>
					<Route
						path="/produtos/novo"
						element={
							<OwnerOnlyGuard>
								{guard(routePermissions.canAccessProducts, <AddProductPageClient />)}
							</OwnerOnlyGuard>
						}
					/>
					<Route
						path="/produtos/ver"
						element={
							<OwnerOnlyGuard>
								{guard(routePermissions.canAccessProducts, <ViewProductPageClient />)}
							</OwnerOnlyGuard>
						}
					/>
					<Route
						path="/suporte"
						element={guard(
							routePermissions.canAccessSupport,
							<OwnerOnlyGuard>
								<SupportPageClient />
							</OwnerOnlyGuard>
						)}
					/>
					<Route
						path="/meu-plano"
						element={
							<OwnerOnlyGuard>
								<MyPlanPage />
							</OwnerOnlyGuard>
						}
					/>
					<Route
						path="/solicitacoes"
						element={
							<OwnerOnlyGuard>
								<ProductRequestsPageClient />
							</OwnerOnlyGuard>
						}
					/>
					<Route
						path="/parcelamento"
						element={guard(routePermissions.canAccessParcelling, <ParcellingPageClient />)}
					/>
					<Route path="/perfil" element={<ProfilePageClient />} />
					<Route
						path="/diario-de-campo"
						element={
							<OwnerOnlyGuard>
								<FieldDiaryPageClient />
							</OwnerOnlyGuard>
						}
					/>
					<Route path="/analises" element={guard(canAccessSamples, <AnalysesPageClient />)} />
					<Route path="/analises/novo" element={guard(canAccessSamples, <AddAnalysisPageClient />)} />
					<Route path="/analises/ver" element={guard(canAccessSamples, <ViewAnalysisPageClient />)} />
					<Route path="/perfil/editar" element={<ProfileEditPageClient />} />
				</Route>

				<Route path="*" element={<Navigate to="/" replace />} />
			</Routes>
			{effectiveRole === UserRole.Farmer && <OcrQueueWidget />}
			{pathname !== '/flora-ai' && <FloraSupportWidget />}
		</>
	);
}

function ForBindingWrapper({ children }: Readonly<{ children: React.ReactNode }>) {
	const { user } = useAuth();
	return <ForBindingProvider currentUserId={user?._id ?? null}>{children}</ForBindingProvider>;
}

const queryClient = new QueryClient();

function App() {
	const getAppUrl = createGetAppUrl(APP_ROUTES);
	const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

	const content = (
		<ThemeProvider>
			<AuthProvider>
				<QueryClientProvider client={queryClient}>
					<ForBindingWrapper>
						<AppNavigationProvider config={{ getAppUrl }}>
							<ToastProvider>
								<ThemePreferenceProvider>
									<BrowserRouter>
										<OcrQueueProvider>
											<NotificationProvider>
												<AppRoutes />
											</NotificationProvider>
										</OcrQueueProvider>
									</BrowserRouter>
								</ThemePreferenceProvider>
							</ToastProvider>
						</AppNavigationProvider>
					</ForBindingWrapper>
				</QueryClientProvider>
			</AuthProvider>
		</ThemeProvider>
	);

	return googleClientId ? (
		<GoogleOAuthProvider clientId={googleClientId}>{content}</GoogleOAuthProvider>
	) : (
		content
	);
}

export default App;
